/* HARUM (HAR-230): Personal Shopper page.

   Replaces customOrderVercel.js, which was jQuery-bound to the ported Expanse
   markup. The estimate rules below are a faithful port of that file so the
   number a shopper sees — and the number CS sees in the confirmation email —
   does not move: KRW converted at harum's own rate, USD$5 service fee, USD$5
   per second-hand item, and a weight-banded international shipping fee.

   Two behaviours that were broken before are fixed here on purpose:
   - the old AJAX call had an empty `error` handler, so a failed submit left
     the shopper staring at "the form is submitting..." forever;
   - no `<form>` element is used at all, which sidesteps the Shopify
     `captcha-bootstrap` script that force-submits contact forms (see HAR-148).
*/
(function () {
  'use strict';

  var IS_LOCAL = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  var API_BASE = IS_LOCAL ? 'http://localhost:3000' : 'https://supply.harum.io';
  var PREVIEW_URL = API_BASE + '/api/public/product-preview';
  var SUBMIT_URL = API_BASE + '/api/custom-orders';
  var RATE_URL = 'https://searchkorea.harum.io/api/harum/rate';
  var QUOTE_URL = API_BASE + '/api/public/custom-order-quote';
  var QUOTE_DEBOUNCE_MS = 600;
  var QUOTE_TIMEOUT_MS = 10000;
  var MATCH_URL = API_BASE + '/api/public/catalog-match';
  var MATCH_DEBOUNCE_MS = 800;
  var MATCH_TIMEOUT_MS = 10000;
  /* The match endpoint takes at most this many titles per call. */
  var MATCH_MAX_ITEMS = 10;
  /* The quote endpoint takes at most this many item lines. */
  var QUOTE_MAX_ITEMS = 30;
  /* Longest item title the quote endpoint takes. */
  var QUOTE_TITLE_MAX = 300;
  var LANE_LABELS = {
    express: 'Express (FedEx/DHL)',
    economy: 'Economy Shipping with Tracking',
    consolidate: 'Ship with my next Harumio order \u2014 USD$0 shipping now'
  };
  var CONSOLIDATE_DESC = 'Shipping is charged once, on the combined parcel.';
  var LANE_EXPRESS_DAYS = '5 - 7 days';
  /* Items per service-fee unit, as the invoice counts them. */
  var FEE_UNIT_ITEMS = 30;

  /* harum's own KRW-per-USD rate (includes PayPal fees). The fallback matches
     the value customOrderVercel.js shipped with. */
  var DEFAULT_RATE = 1230;

  /* Ported verbatim from customOrderVercel.js: express couriers cost more per
     shipment, and every extra 0.5 KG over the first kilo adds USD$5. */
  var SHIPPING_BASE = { 'dhl/fedex': 34, dhl: 34, fedex: 34 };
  var SHIPPING_BASE_ECONOMY = 20;
  var OVERWEIGHT_PER_KG = 10;

  /* Which couriers each destination may pick, first entry preselected. */
  var METHODS_BY_COUNTRY = {
    Malaysia: ['skynet'],
    Singapore: ['cj'],
    Philippines: ['ninjavan', 'dhl/fedex'],
    Taiwan: ['chunghwa', 'dhl/fedex'],
    Japan: ['sagawa', 'dhl/fedex'],
    'United States': ['dhl/fedex', 'usps']
  };
  var DEFAULT_METHODS = ['dhl/fedex', 'airparcel'];

  /* Destinations that levy import duty on the receiver. The old form disabled
     submit until the shopper accepted the charge; same rule, nicer control. */
  var TAX_COUNTRIES = [
    'France', 'Ireland', 'Greece', 'Philippines', 'Canada', 'Brazil',
    'Tunisia', 'Peru', 'Mexico', 'United Kingdom'
  ];

  function money(usd) {
    return 'USD$' + usd.toFixed(2);
  }

  function won(krw) {
    return '₩' + Math.round(krw).toLocaleString('en-US');
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  /* Links added manually never pass through the preview API, so they are the
     one path where a `javascript:` URL could reach an href. */
  function safeHref(value) {
    return /^https?:\/\//i.test(value || '') ? value : '';
  }

  /* A hand-typed link usually arrives without its scheme; store it with one so
     the CS admin, the confirmation email and the item list all get a URL they
     can actually follow. */
  function normalizeUrl(value) {
    var url = String(value == null ? '' : value).trim();
    if (!url) return '';
    return /^https?:\/\//i.test(url) ? url : 'https://' + url;
  }

  /* ------------------------------------------------------- catalog match */

  /* One key per title, so the same product pasted twice is asked about once. */
  function matchKey(title) {
    return String(title || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  function isHarumUrl(url) {
    return /^https?:\/\/([^/?#]*\.)?harum\.io(?=[/?#:]|$)/i.test(String(url || ''));
  }

  /* The items still to ask about: a title, not already answered or in flight,
     not already a harum.io link. At most MATCH_MAX_ITEMS, one per title. */
  function titlesToCheck(items, known) {
    var out = [];
    var seen = {};
    items.forEach(function (item) {
      var title = String(item.nameOriginal || item.name || '').trim();
      var key = matchKey(title);
      if (!key || seen[key] || known[key] || out.length >= MATCH_MAX_ITEMS) return;
      seen[key] = true;
      if (isHarumUrl(item.url)) return;
      out.push({ key: key, title: title, url: safeHref(item.url) });
    });
    return out;
  }

  /* {key: {title, url, similarity}} from the endpoint's answer to `asked`
     (the array sent, in order). Anything unusable is ignored. */
  function parseMatches(data, asked) {
    var found = {};
    if (!data || data.ok !== true || !Array.isArray(data.matches)) return found;
    data.matches.forEach(function (m) {
      if (!m || typeof m !== 'object') return;
      var ask = asked[m.index];
      var url = typeof m.url === 'string' ? m.url : '';
      if (!ask || !/^https:\/\//i.test(url)) return;
      found[ask.key] = {
        title: typeof m.title === 'string' ? m.title : '',
        url: url,
        similarity: finiteNumber(m.similarity) ? m.similarity : null
      };
    });
    return found;
  }

  /* ------------------------------------------------------ ISO country */

  /* With the iso_country setting the country select holds ISO codes (US, GB)
     and shows the English name. The name is what staff read and what the old
     payload carried; the code is what the quote endpoint prices. */
  /* English name for an ISO code. Shopify translates the option labels in
     non-English storefronts ("Estados Unidos"), but staff and the courier and
     tax rules work with English names, so the label is only a fallback. */
  function englishCountryName(code, fallback) {
    try {
      if (typeof Intl !== 'undefined' && Intl.DisplayNames && /^[A-Za-z]{2}$/.test(code || '')) {
        var name = new Intl.DisplayNames(['en'], { type: 'region' }).of(String(code).toUpperCase());
        if (name && name.toUpperCase() !== String(code).toUpperCase()) return name;
      }
    } catch (err) {
      /* unsupported: use the label */
    }
    return fallback || '';
  }

  function countryName(select, iso) {
    if (!iso) return select.value;
    if (!select.value) return '';
    var options = select.options || [];
    var option = options[select.selectedIndex];
    var label = option ? String(option.textContent || '').trim() : '';
    return englishCountryName(select.value, label);
  }

  /* The option value that stands for a saved country. In ISO mode the saved
     value is an English name (older saves too), so match it against each
     option's English name, its label, or its code. */
  function findCountryValue(select, saved, iso) {
    if (!saved) return null;
    var wanted = String(saved).trim().toLowerCase();
    var options = Array.prototype.slice.call(select.options || []);
    for (var i = 0; i < options.length; i++) {
      var option = options[i];
      if (!option.value) continue;
      if (!iso) {
        if (option.value === saved) return option.value;
        continue;
      }
      var label = String(option.textContent || '').trim().toLowerCase();
      var english = englishCountryName(option.value, '').toLowerCase();
      if (label === wanted || english === wanted || option.value.toLowerCase() === wanted) {
        return option.value;
      }
    }
    return null;
  }

  /* ---------------------------------------------------- saved request list */

  /* The unsent list survives reloads, a second tab and a stray close, because
     a shopper often leaves to look at a Korean shop and comes back. Only the
     request itself is kept — never the shopper's name or email. */
  var STORAGE_KEY = 'harum.ps.v1';
  var SAVE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var MAX_SAVED_ITEMS = 50;

  function storage() {
    /* Merely reading window.localStorage throws when site data is blocked. */
    try {
      return window.localStorage || null;
    } catch (err) {
      return null;
    }
  }

  function cleanSavedItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var price = Number(raw.price);
    var quantity = parseInt(raw.quantity, 10);
    if (typeof raw.name !== 'string' || !raw.name.trim()) return null;
    if (!isFinite(price) || price <= 0) return null;
    return {
      id: isFinite(Number(raw.id)) ? Number(raw.id) : 0,
      url: typeof raw.url === 'string' ? raw.url : '',
      name: raw.name,
      nameOriginal: typeof raw.nameOriginal === 'string' ? raw.nameOriginal : null,
      price: price,
      quantity: isFinite(quantity) && quantity >= 1 ? quantity : 1,
      comments: typeof raw.comments === 'string' ? raw.comments : '',
      secondHand: !!raw.secondHand,
      image: typeof raw.image === 'string' && raw.image ? raw.image : null,
      domesticShipping:
        typeof raw.domesticShipping === 'number' ? raw.domesticShipping : null
    };
  }

  /* Returns {items, country, weight} or null when there is nothing usable
     (missing, corrupt, wrong version, older than 30 days, storage blocked). */
  function loadSavedList(now) {
    var store = storage();
    if (!store) return null;
    try {
      var raw = store.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || data.v !== 1 || !Array.isArray(data.items)) return null;
      var savedAt = Number(data.savedAt);
      if (!isFinite(savedAt) || now - savedAt >= SAVE_TTL_MS) {
        store.removeItem(STORAGE_KEY);
        return null;
      }
      var items = data.items
        .slice(0, MAX_SAVED_ITEMS)
        .map(cleanSavedItem)
        .filter(Boolean);
      if (!items.length) return null;
      return {
        items: items,
        country: typeof data.country === 'string' ? data.country : '',
        weight: typeof data.weight === 'string' ? data.weight : ''
      };
    } catch (err) {
      return null;
    }
  }

  /* An empty list removes the entry, which is also how a successful submit
     clears it. */
  function writeSavedList(snapshot, now) {
    var store = storage();
    if (!store) return;
    try {
      var items = (snapshot.items || [])
        .slice(0, MAX_SAVED_ITEMS)
        .map(cleanSavedItem)
        .filter(Boolean);
      if (!items.length) {
        store.removeItem(STORAGE_KEY);
        return;
      }
      store.setItem(
        STORAGE_KEY,
        JSON.stringify({
          v: 1,
          items: items,
          country: snapshot.country || '',
          weight: snapshot.weight || '',
          savedAt: now
        })
      );
    } catch (err) {
      /* quota or blocked storage: the list just will not survive a reload */
    }
  }

  /* Two links count as the same item when only tracking noise, the scheme,
     a leading www., the fragment or a trailing slash differ. */
  function canonicalUrl(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return '';
    var url = /^https?:\/\//i.test(raw) ? raw : 'https://' + raw;
    try {
      var parsed = new URL(url);
      var params = [];
      parsed.searchParams.forEach(function (v, k) {
        if (!/^(utm_|fbclid$|gclid$)/i.test(k)) params.push([k, v]);
      });
      params.sort(function (a, b) {
        return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0;
      });
      var query = params
        .map(function (pair) {
          return encodeURIComponent(pair[0]) + '=' + encodeURIComponent(pair[1]);
        })
        .join('&');
      return (
        parsed.hostname.toLowerCase().replace(/^www\./, '') +
        parsed.pathname.replace(/\/+$/, '') +
        (query ? '?' + query : '')
      );
    } catch (err) {
      return url.toLowerCase();
    }
  }

  function findItemIndexByUrl(items, url) {
    var wanted = canonicalUrl(url);
    if (!wanted) return -1;
    for (var i = 0; i < items.length; i++) {
      if (canonicalUrl(items[i].url) === wanted) return i;
    }
    return -1;
  }

  /* ----------------------------------------------------------- live quote */

  function finiteNumber(value) {
    return typeof value === 'number' && isFinite(value);
  }

  /* Shipping options the quote endpoint may add next to the Express lines.
     An option we cannot show is dropped, never fatal: the top-level lines are
     always a complete Express quote. */
  function parseOptions(raw) {
    if (!Array.isArray(raw)) return [];
    var options = [];
    raw.forEach(function (o) {
      if (!o || typeof o !== 'object') return;
      if (o.lane !== 'express' && o.lane !== 'economy' && o.lane !== 'consolidate') return;
      if (!finiteNumber(o.total) || o.total < 0 || !Array.isArray(o.lines)) return;
      var lines = [];
      for (var i = 0; i < o.lines.length; i++) {
        var line = o.lines[i];
        if (!line || typeof line.label !== 'string' || !finiteNumber(line.usd)) return;
        lines.push({ kind: String(line.kind || ''), label: line.label, usd: line.usd });
      }
      var shipping = finiteNumber(o.shipping) ? o.shipping : null;
      if (shipping === null) {
        lines.forEach(function (line) {
          if (line.kind === 'shipping' && shipping === null) shipping = line.usd;
        });
      }
      var days = o.transitDays;
      var transitDays =
        days && finiteNumber(days.low) && finiteNumber(days.high) && days.low > 0 && days.high >= days.low
          ? { low: days.low, high: days.high }
          : null;
      options.push({
        lane: o.lane,
        lines: lines,
        total: o.total,
        shipping: shipping,
        transitDays: transitDays,
        note: typeof o.note === 'string' && o.note.trim() ? o.note : ''
      });
    });
    return options;
  }

  /* The endpoint is built in parallel with this page and may change shape, so
     take only what is needed and refuse the whole thing if a number we would
     show is missing. Returns null for anything unusable. */
  function parseQuote(data) {
    if (!data || typeof data !== 'object') return null;
    if (!finiteNumber(data.total) || data.total < 0) return null;
    if (!Array.isArray(data.lines)) return null;
    var lines = [];
    for (var i = 0; i < data.lines.length; i++) {
      var line = data.lines[i];
      if (!line || typeof line.label !== 'string' || !finiteNumber(line.usd)) return null;
      lines.push({ kind: String(line.kind || ''), label: line.label, usd: line.usd });
    }
    var fx = data.fx;
    var krwPerUsd = fx && finiteNumber(fx.krwPerUsd) && fx.krwPerUsd > 0 ? fx.krwPerUsd : null;
    var ship = data.shipping && typeof data.shipping === 'object' ? data.shipping : null;
    var notes = Array.isArray(data.notes)
      ? data.notes.filter(function (n) {
          return typeof n === 'string' && n.trim();
        })
      : [];
    if (!notes.length && typeof data.note === 'string' && data.note.trim()) {
      notes.push(data.note);
    }
    return {
      total: data.total,
      lines: lines,
      krwPerUsd: krwPerUsd,
      billedKg: ship && finiteNumber(ship.billedKg) && ship.billedKg > 0 ? ship.billedKg : null,
      weightSource: ship && typeof ship.weightSource === 'string' ? ship.weightSource : '',
      /* no shipping block = checkout-rates mode: shipping and duties are
         worked out at checkout, so the total excludes them */
      shippingAtCheckout: !ship,
      notes: notes,
      options: parseOptions(data.options)
    };
  }

  /* Request body for the quote endpoint, or null when there is nothing to
     quote yet (no items, no country, more lines than the endpoint takes). */
  function buildQuoteRequest(items, country, weightText, withTitles) {
    if (!items.length || !country || items.length > QUOTE_MAX_ITEMS) return null;
    var request = {
      country: country,
      items: items.map(function (item) {
        var line = {
          priceKrw: item.price,
          quantity: item.quantity,
          secondHand: !!item.secondHand,
          domesticShippingKrw: finiteNumber(item.domesticShipping) ? item.domesticShipping : 0
        };
        /* The title lets the server check which lanes this item may ship by. */
        var title = String(item.nameOriginal || item.name || '').trim();
        if (withTitles && title) line.title = title.slice(0, QUOTE_TITLE_MAX);
        return line;
      })
    };
    var weight = parseFloat(weightText);
    if (isFinite(weight) && weight > 0) request.weightKg = weight;
    return request;
  }

  function init(root) {
    var $ = function (name) {
      return root.querySelector('[data-ps-' + name + ']');
    };

    var state = {
      items: [],
      rate: DEFAULT_RATE,
      editingId: null,
      nextId: 1,
      draft: null,
      /* what was in the dialog when it was closed without saving (HAR-239) */
      stash: null,
      /* {key, data} of the latest server quote, and whether one is in flight */
      quote: null,
      quoteLoading: false,
      quoteSeq: 0,
      /* the shipping lane the shopper picked, and a note when it went away */
      lane: 'express',
      laneNote: '',
      /* matchKey(title) -> true (asked, no match) | {title, url, similarity} */
      matches: {},
      matchSending: {}
    };
    var matchTimer = null;
    var quoteTimer = null;

    /* Set while saveDialog() closes the dialog, so a saved item is not also
       kept as an unfinished draft. */
    var skipStash = false;

    /* Off unless the section setting is on: the list is then kept in
       localStorage between visits. */
    var persistList = root.dataset.persistList === 'true';

    /* Off unless the section setting is on: the summary then shows the server's
       quote (one formula, one exchange rate) instead of the local estimate. */
    var liveQuote = root.dataset.liveQuote === 'true';

    /* Off unless the section setting is on (and Shopify listed the market's
       countries): the country select then holds ISO codes. */
    var isoCountry = root.dataset.isoCountry === 'true';

    /* Off unless the section setting is on: items the catalog already stocks
       get a "ready to ship from our warehouse" card. */
    var catalogMatch = root.dataset.catalogMatch === 'true';

    /* Off unless the section setting is on (it needs live_quote): shipping
       options from the quote replace the method select with lane cards. */
    var shippingLanes = liveQuote && root.dataset.shippingLanes === 'true';

    /* Off unless the setting is on and the shopper is logged in (the combined
       parcel needs an account to attach to). */
    var consolidateOption =
      shippingLanes && root.dataset.consolidateOption === 'true' && !!root.dataset.customer;

    var serviceFee = parseFloat(root.dataset.serviceFee) || 0;
    var secondHandFee = parseFloat(root.dataset.secondHandFee) || 0;

    var els = {
      urlInput: $('url-input'),
      addBtn: $('add'),
      addLabel: $('add-label'),
      pasteError: $('paste-error'),
      manualBtn: $('manual'),
      resumeBtn: $('resume'),
      itemList: $('item-list'),
      empty: $('empty'),
      details: $('details'),
      name: $('name'),
      email: $('email'),
      country: $('country'),
      method: $('method'),
      methodField: $('method-field'),
      lanes: $('lanes'),
      weight: $('weight'),
      tax: $('tax'),
      taxCopy: $('tax-copy'),
      taxAgree: $('tax-agree'),
      submit: $('submit'),
      submitLabel: $('submit-label'),
      submitError: $('submit-error'),
      sumItems: $('sum-items'),
      sumSecondHandRow: $('sum-secondhand-row'),
      sumSecondHand: $('sum-secondhand'),
      sumShipping: $('sum-shipping'),
      sumService: $('sum-service'),
      sumTotal: $('sum-total'),
      sumStatic: $('sum-static'),
      sumLive: $('sum-live'),
      sumMeta: $('sum-meta'),
      sumTotalLabel: $('sum-total-label'),
      dialog: $('dialog'),
      dialogClose: $('dialog-close'),
      dialogUrl: $('dialog-url'),
      dialogPreview: $('dialog-preview'),
      dialogImage: $('dialog-image'),
      dialogName: $('dialog-name'),
      dialogQty: $('dialog-qty'),
      dialogPrice: $('dialog-price'),
      dialogPriceNote: $('dialog-price-note'),
      dialogComment: $('dialog-comment'),
      dialogSecondHand: $('dialog-secondhand'),
      dialogSubtotal: $('dialog-subtotal'),
      dialogSave: $('dialog-save'),
      qtyUp: $('qty-up'),
      qtyDown: $('qty-down'),
      review: $('review'),
      reviewBody: $('review-body'),
      reviewClose: $('review-close'),
      reviewBack: $('review-back'),
      reviewConfirm: $('review-confirm'),
      done: $('done'),
      doneClose: $('done-close'),
      /* HAR-260: the "What you pay for" popup. Absent when the section has no
         cost rows, so every use of it is guarded. */
      cost: $('cost'),
      costClose: $('cost-close')
    };

    /* ---------------------------------------------------------------- rate */

    /* The sidebar can carry a "Conversion rate: {rate} KRW = $1.00 USD" line;
       fill the token with whatever rate the estimate is actually using so the
       two can never disagree. */
    function renderRateNotes() {
      root.querySelectorAll('[data-ps-rate-note]').forEach(function (node) {
        if (!node.dataset.template) node.dataset.template = node.textContent;
        node.textContent = node.dataset.template.replace(
          /\{rate\}/g,
          Math.round(state.rate).toLocaleString('en-US')
        );
      });
    }

    fetch(RATE_URL)
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (data && data.rate > 0) {
          state.rate = data.rate;
          renderSummary();
        }
      })
      .catch(function () {
        /* keep DEFAULT_RATE — the estimate is explicitly an estimate */
      })
      .then(renderRateNotes);

    /* ------------------------------------------------------------- estimate */

    /* Sub-USD1 lines were floored to USD$1 in the old form; keep that so a
       ₩500 sticker still carries its share of the handling cost. */
    function itemUsd(item) {
      var totalWon = item.price * item.quantity;
      if (totalWon < state.rate) return 1;
      return Math.round((totalWon / state.rate) * 100) / 100;
    }

    function billableWeight() {
      var entered = parseFloat(els.weight.value);
      if (!isFinite(entered) || entered <= 0) return 1;
      var rounded = Math.ceil(entered * 2) / 2;
      return rounded < 1 ? 1 : rounded;
    }

    function shippingUsd() {
      var method = els.method.value;
      var weight = billableWeight();
      /* AirParcel is capped at 2 KG; heavier parcels fall back to express. */
      if (method === 'airparcel' && weight > 2) method = 'dhl/fedex';
      var base = SHIPPING_BASE[method] || SHIPPING_BASE_ECONOMY;
      return base + Math.max(0, weight - 1) * OVERWEIGHT_PER_KG;
    }

    /* Same rule as the invoice: one service fee per started 30 regular
       items, plus one second-hand fee per second-hand item. A request made
       only of second-hand items therefore pays no base service fee. */
    function estimate() {
      var items = 0;
      var secondHand = 0;
      var regular = 0;
      state.items.forEach(function (item) {
        items += itemUsd(item);
        if (item.secondHand) secondHand += secondHandFee;
        else regular += 1;
      });
      var shipping = state.items.length ? shippingUsd() : 0;
      var service = serviceFee * Math.ceil(regular / FEE_UNIT_ITEMS);
      return {
        items: items,
        secondHand: secondHand,
        secondHandCount: state.items.filter(function (i) {
          return i.secondHand;
        }).length,
        shipping: shipping,
        service: service,
        total: items + secondHand + shipping + service
      };
    }

    /* -------------------------------------------------------------- render */

    /* ---------------------------------------------------- live quote state */

    /* The country's English name, whatever the select's values are. */
    function selectedCountry() {
      return countryName(els.country, isoCountry);
    }

    function currentQuoteRequest() {
      return buildQuoteRequest(state.items, els.country.value, els.weight.value, shippingLanes);
    }

    /* The quote for exactly what is on screen now, or null (none yet, still
       loading, or the inputs moved on since it was fetched). */
    function freshQuote() {
      if (!liveQuote || !state.quote) return null;
      var request = currentQuoteRequest();
      if (!request || JSON.stringify(request) !== state.quote.key) return null;
      return shippingLanes ? laneView(state.quote.data) : state.quote.data;
    }

    /* ------------------------------------------------------ shipping lanes */

    /* The options the shopper may choose between. Express is always first. */
    function visibleOptions(quote) {
      return quote.options.filter(function (o) {
        return (
          o.lane === 'express' || o.lane === 'economy' || (o.lane === 'consolidate' && consolidateOption)
        );
      });
    }

    function findOption(quote, lane) {
      for (var i = 0; i < quote.options.length; i++) {
        if (quote.options[i].lane === lane) return quote.options[i];
      }
      return null;
    }

    /* The lane in force for this quote: the shopper's pick when the quote
       still offers it, otherwise Express. */
    function laneFor(quote) {
      if (!shippingLanes || !quote || visibleOptions(quote).length < 2) return 'express';
      var visible = visibleOptions(quote);
      for (var i = 0; i < visible.length; i++) {
        if (visible[i].lane === state.lane) return state.lane;
      }
      return 'express';
    }

    /* The quote as the shopper sees it: the chosen option's lines, total and
       note instead of the Express ones. */
    function laneView(quote) {
      var lane = laneFor(quote);
      if (lane === 'express') return quote;
      var option = findOption(quote, lane);
      if (!option) return quote;
      return Object.assign({}, quote, {
        lines: option.lines,
        total: option.total,
        notes: option.note ? [option.note] : []
      });
    }

    /* What the request's shipMethod says. */
    function submitMethod() {
      if (!shippingLanes) return els.method.value;
      var quote = state.quote ? state.quote.data : null;
      var fresh = freshQuote();
      if (!quote || !fresh || visibleOptions(quote).length < 2) return els.method.value;
      return laneFor(quote);
    }

    function formatDays(days) {
      return 'about ' + days.low + '–' + days.high + ' days';
    }

    function laneCard(option, checked) {
      var days = option.transitDays
        ? formatDays(option.transitDays)
        : option.lane === 'express'
          ? LANE_EXPRESS_DAYS
          : '';
      var price =
        option.lane === 'consolidate' || option.shipping === null ? '' : money(option.shipping);
      return (
        '<label class="harum-ps__lane' + (checked ? ' is-selected' : '') + '">' +
        '<input type="radio" name="harum-ps-lane" value="' + escapeHtml(option.lane) + '"' +
        (checked ? ' checked' : '') + '>' +
        '<span class="harum-ps__lane-title">' + escapeHtml(LANE_LABELS[option.lane]) + '</span>' +
        '<span class="harum-ps__lane-price">' + escapeHtml(price) + '</span>' +
        (days ? '<span class="harum-ps__lane-days">' + escapeHtml(days) + '</span>' : '') +
        (option.lane === 'consolidate'
          ? '<span class="harum-ps__lane-desc">' + escapeHtml(CONSOLIDATE_DESC) + '</span>'
          : '') +
        '</label>'
      );
    }

    /* Lane cards replace the method select while the quote offers a choice;
       with no options everything looks exactly as before. */
    function renderLanes() {
      if (!shippingLanes || !els.lanes) return;
      var quote = state.quote ? state.quote.data : null;
      var visible = quote ? visibleOptions(quote) : [];
      var cards = visible.length > 1;
      var chosen = laneFor(quote);
      var html = '';
      if (cards) {
        html += '<div class="harum-ps__lane-list" role="radiogroup" aria-label="Shipping">';
        visible.forEach(function (option) {
          html += laneCard(option, option.lane === chosen);
        });
        html += '</div>';
      }
      if (state.laneNote) {
        html += '<p class="harum-ps__lane-note">' + escapeHtml(state.laneNote) + '</p>';
      }
      els.lanes.innerHTML = html;
      els.lanes.hidden = !html;
      if (els.methodField) els.methodField.hidden = cards;
    }

    /* A new quote arrived: if the lane the shopper had picked is gone, say so
       and go back to Express rather than keep a price that no longer exists. */
    function reconcileLane(quote) {
      if (!shippingLanes || state.lane === 'express') return;
      if (laneFor(quote) !== state.lane) {
        state.laneNote =
          LANE_LABELS[state.lane].split(' \u2014')[0] + ' is not available for this request, so it is shown with Express.';
        state.lane = 'express';
      }
    }

    function scheduleQuote() {
      if (!liveQuote) return;
      clearTimeout(quoteTimer);
      var request = currentQuoteRequest();
      if (!request) {
        state.quote = null;
        state.quoteLoading = false;
        state.quoteSeq += 1;
        renderSummary();
        return;
      }
      var key = JSON.stringify(request);
      if (state.quote && state.quote.key === key) {
        state.quoteLoading = false;
        renderSummary();
        return;
      }
      state.quoteLoading = true;
      renderSummary();
      var seq = ++state.quoteSeq;
      quoteTimer = setTimeout(function () {
        fetchQuote(request, key, seq);
      }, QUOTE_DEBOUNCE_MS);
    }

    function fetchQuote(request, key, seq) {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timeout = controller
        ? setTimeout(function () {
            controller.abort();
          }, QUOTE_TIMEOUT_MS)
        : null;
      fetch(QUOTE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal: controller ? controller.signal : undefined
      })
        .then(function (res) {
          if (!res.ok) throw new Error('quote failed: ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var quote = parseQuote(data);
          if (!quote) throw new Error('unusable quote');
          if (seq !== state.quoteSeq) return;
          state.quote = { key: key, data: quote };
          state.quoteLoading = false;
          reconcileLane(quote);
          /* keep the "Conversion rate" notes on the page in step with the
             rate the quote actually used */
          if (quote.krwPerUsd) state.rate = quote.krwPerUsd;
          renderRateNotes();
          renderSummary();
        })
        .catch(function () {
          if (seq !== state.quoteSeq) return;
          /* fall back to the local formula, labelled as rough */
          state.quote = null;
          state.quoteLoading = false;
          renderSummary();
        })
        .then(function () {
          if (timeout) clearTimeout(timeout);
        });
    }

    function weightSourceText(source) {
      if (!source) return '';
      return /customer|entered|user|provided/i.test(source)
        ? ' (your weight)'
        : ' (our estimate)';
    }

    /* Everything that explains a server quote, as text lines. */
    function quoteMeta(quote, loading) {
      var lines = [];
      if (loading) lines.push('Updating your quote…');
      if (quote.krwPerUsd) {
        lines.push('Rate used ₩' + Math.round(quote.krwPerUsd).toLocaleString('en-US') + ' = $1');
      }
      if (quote.billedKg) {
        lines.push('Billed weight ' + quote.billedKg + ' KG' + weightSourceText(quote.weightSource));
      }
      quote.notes.forEach(function (note) {
        lines.push(note);
      });
      lines.push(
        'We confirm the final amount by email before you pay.' +
          ' The exchange rate and carrier rates can move a little in between.'
      );
      return lines;
    }

    var TOTAL_LABEL = 'Estimated total';
    var TOTAL_LABEL_NO_SHIPPING =
      'Total before shipping and duties (calculated at checkout)';

    function totalLabel(quote) {
      return quote && quote.shippingAtCheckout ? TOTAL_LABEL_NO_SHIPPING : TOTAL_LABEL;
    }

    function renderSummaryLive(quote, loading) {
      els.sumStatic.hidden = true;
      els.sumLive.hidden = false;
      els.sumLive.innerHTML = '';
      quote.lines.forEach(function (line) {
        var row = document.createElement('div');
        var dt = document.createElement('dt');
        var dd = document.createElement('dd');
        dt.textContent = line.label;
        dd.textContent = money(line.usd);
        row.appendChild(dt);
        row.appendChild(dd);
        els.sumLive.appendChild(row);
      });
      els.sumTotal.textContent = money(quote.total);
      if (els.sumTotalLabel) els.sumTotalLabel.textContent = totalLabel(quote);
      showMeta(quoteMeta(quote, loading));
    }

    function showMeta(lines) {
      els.sumMeta.innerHTML = '';
      lines.forEach(function (text) {
        var p = document.createElement('p');
        p.textContent = text;
        els.sumMeta.appendChild(p);
      });
      els.sumMeta.hidden = lines.length === 0;
    }

    function renderSummary() {
      if (shippingLanes) renderLanes();
      if (liveQuote && state.items.length && els.sumLive) {
        /* While a new quote loads, keep showing the last one rather than
           flashing the local numbers. */
        var shown = state.quote ? (shippingLanes ? laneView(state.quote.data) : state.quote.data) : null;
        if (shown) {
          renderSummaryLive(shown, state.quoteLoading && !freshQuote());
          return;
        }
      }
      if (liveQuote && els.sumLive) {
        els.sumStatic.hidden = false;
        els.sumLive.hidden = true;
        if (els.sumTotalLabel) els.sumTotalLabel.textContent = TOTAL_LABEL;
        showMeta(
          state.items.length
            ? [
                state.quoteLoading
                  ? 'Getting your quote…'
                  : !els.country.value
                    ? 'Choose your shipping country to get an exact quote.'
                    : 'Rough estimate. We confirm the exact amount by email before any payment.'
              ]
            : []
        );
      }
      var e = estimate();
      els.sumItems.textContent = money(e.items);
      els.sumShipping.textContent = money(e.shipping);
      els.sumService.textContent = money(e.service);
      els.sumTotal.textContent = money(e.total);
      els.sumSecondHandRow.hidden = e.secondHandCount === 0;
      els.sumSecondHand.textContent =
        money(e.secondHand) + ' (' + e.secondHandCount + ' item' +
        (e.secondHandCount === 1 ? '' : 's') + ')';
    }

    /* Written after every render of the list; `fromStorage` renders what was
       just read from storage and must not write it straight back (that would
       bump savedAt and ping-pong between two tabs). */
    function persist() {
      if (!persistList) return;
      writeSavedList(
        { items: state.items, country: selectedCountry(), weight: els.weight.value },
        Date.now()
      );
    }

    function renderItems(fromStorage) {
      els.itemList.innerHTML = '';
      state.items.forEach(function (item, index) {
        var li = document.createElement('li');
        li.className = 'harum-ps__item';
        li.innerHTML =
          '<div class="harum-ps__item-thumb">' +
          (item.image
            ? '<img src="' + escapeHtml(item.image) +
              '" alt="" loading="lazy" width="80" height="80">'
            : '<span class="harum-ps__item-thumb-empty" aria-hidden="true"></span>') +
          '</div>' +
          '<div class="harum-ps__item-body">' +
          '<p class="harum-ps__item-name">' + escapeHtml(item.name) + '</p>' +
          '<a class="harum-ps__item-url" href="' + escapeHtml(safeHref(item.url)) +
          '" target="_blank" rel="noopener nofollow">' + escapeHtml(item.url) + '</a>' +
          (item.comments
            ? '<p class="harum-ps__item-note">' + escapeHtml(item.comments) + '</p>'
            : '') +
          (item.secondHand
            ? '<p class="harum-ps__item-tag">Second-hand</p>'
            : '') +
          (catalogMatch ? matchHtml(item, index) : '') +
          '</div>' +
          '<div class="harum-ps__item-figures">' +
          '<p class="harum-ps__item-price">' + won(item.price * item.quantity) + '</p>' +
          '<p class="harum-ps__item-qty">Qty ' + item.quantity + '</p>' +
          '<div class="harum-ps__item-actions">' +
          '<button type="button" data-edit="' + index + '">Edit</button>' +
          '<button type="button" data-remove="' + index + '">Remove</button>' +
          '</div></div>';
        els.itemList.appendChild(li);
      });

      els.empty.hidden = state.items.length > 0;
      els.details.hidden = state.items.length === 0;
      renderSummary();
      scheduleQuote();
      if (catalogMatch) scheduleMatch();
      if (fromStorage !== true) persist();
    }

    /* ------------------------------------------------------- catalog match */

    function matchFor(item) {
      var found = state.matches[matchKey(item.nameOriginal || item.name)];
      return found && typeof found === 'object' && !isHarumUrl(item.url) ? found : null;
    }

    function matchHtml(item, index) {
      var found = matchFor(item);
      if (!found) return '';
      return (
        '<div class="harum-ps__match">' +
        '<p class="harum-ps__match-text">We already stock this \u2014 ready to ship from our warehouse</p>' +
        '<a class="harum-ps__match-link" href="' + escapeHtml(found.url) +
        '" target="_blank" rel="noopener">View &amp; add to cart</a>' +
        '<button type="button" class="harum-ps__match-remove" data-match-remove="' + index +
        '">Remove from this request</button>' +
        '</div>'
      );
    }

    function scheduleMatch() {
      clearTimeout(matchTimer);
      var asked = titlesToCheck(state.items, Object.assign({}, state.matches, state.matchSending));
      if (!asked.length) return;
      matchTimer = setTimeout(function () {
        fetchMatches(titlesToCheck(state.items, Object.assign({}, state.matches, state.matchSending)));
      }, MATCH_DEBOUNCE_MS);
    }

    function fetchMatches(asked) {
      if (!asked.length) return;
      asked.forEach(function (a) {
        state.matchSending[a.key] = true;
      });
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timeout = controller
        ? setTimeout(function () {
            controller.abort();
          }, MATCH_TIMEOUT_MS)
        : null;
      fetch(MATCH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: asked.map(function (a) {
            return a.url ? { title: a.title, url: a.url } : { title: a.title };
          })
        }),
        signal: controller ? controller.signal : undefined
      })
        .then(function (res) {
          if (!res.ok) throw new Error('match failed: ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var found = parseMatches(data, asked);
          asked.forEach(function (a) {
            state.matches[a.key] = found[a.key] || true;
          });
          renderItems(true);
        })
        .catch(function () {
          /* silent: the request works the same without the hint, and the
             next change to the list asks again */
        })
        .then(function () {
          if (timeout) clearTimeout(timeout);
          asked.forEach(function (a) {
            delete state.matchSending[a.key];
          });
        });
    }

    /* -------------------------------------------------------------- dialog */

    /* <dialog>.showModal() gives us the focus trap, Escape handling and inert
       background for free; we only add the body scroll lock on top. */
    function openOverlay(dialog) {
      dialog.showModal();
      document.body.classList.add('harum-ps-locked');
    }

    function closeOverlay(dialog) {
      dialog.close();
    }

    var overlays = [els.dialog, els.review, els.done];
    /* The item dialog deliberately has no click-outside-to-close: a stray click
       used to wipe everything typed into it (HAR-239). The others hold no
       unsaved input, so there the backdrop still closes them. */
    var backdropCloses = [els.review, els.done];
    if (els.cost) {
      overlays.push(els.cost);
      backdropCloses.push(els.cost);
    }

    overlays.forEach(function (dialog) {
      dialog.addEventListener('close', function () {
        if (!root.querySelector('dialog[open]')) {
          document.body.classList.remove('harum-ps-locked');
        }
      });
    });

    /* Clicking the backdrop lands on the dialog element itself. */
    backdropCloses.forEach(function (dialog) {
      dialog.addEventListener('click', function (event) {
        if (event.target !== dialog) return;
        var box = dialog.getBoundingClientRect();
        var inside =
          event.clientX >= box.left && event.clientX <= box.right &&
          event.clientY >= box.top && event.clientY <= box.bottom;
        if (!inside) dialog.close();
      });
    });

    /* ------------------------------------------------- name translation */

    /* Marketplace titles come back in Korean, which most of our shoppers cannot
       read. Chrome/Edge 138+ expose an on-device translator, so where it exists
       we show the item name in English. Everywhere else — Safari, Chrome on
       Android, older builds — nothing runs and the Korean name stays, exactly as
       before. Progressive enhancement only: the name we SUBMIT is always the
       Korean original (see saveDialog / itemName), because that is the string CS
       searches with when they place the buy. */

    /* Hangul Jamo, Compatibility Jamo, Jamo Extended-A/B, Syllables and
       Halfwidth forms — escaped so an encoding mangle cannot silently
       narrow the ranges. */
    var HANGUL =
      /[\u1100-\u11FF\u3130-\u318F\uA960-\uA97F\uAC00-\uD7A3\uD7B0-\uD7FF\uFFA0-\uFFDC]/;
    var translatorPromise = null;

    function getNameTranslator() {
      /* Feature-detect the methods, not just the global: a foreign script can
         claim window.Translator, and a synchronous throw here would take the
         whole add flow down with it. */
      if (
        !window.Translator ||
        typeof window.Translator.availability !== 'function' ||
        typeof window.Translator.create !== 'function'
      ) {
        return Promise.resolve(null);
      }
      if (!translatorPromise) {
        var pair = { sourceLanguage: 'ko', targetLanguage: 'en' };
        translatorPromise = Promise.resolve()
          .then(function () {
            return window.Translator.availability(pair);
          })
          .then(function (availability) {
            if (availability === 'unavailable') return null;
            /* First call downloads the ko->en pack; it needs the user gesture
               that opened this dialog, so never call it off a timer. */
            return window.Translator.create(pair);
          })
          .catch(function () {
            translatorPromise = null; // let the next dialog try again
            return null;
          });
      }
      return translatorPromise;
    }

    function showNameInEnglish() {
      /* Translate only a fresh, marketplace-sourced name. Never while
         editing — the name on the list is the shopper's confirmed wording,
         machine or human — and never a draft that already carries a Korean
         original: re-translating a partially-translated name would replace
         the true marketplace title CS searches with. */
      if (state.editingId !== null) return;
      if (!state.draft || state.draft.nameOriginal) return;

      var korean = els.dialogName.value.trim();
      if (!korean || !HANGUL.test(korean)) return;

      var draft = state.draft;

      /* Returned so it can be awaited in tests; openDialog deliberately does
         not wait on it — the dialog must open at once either way. */
      return getNameTranslator()
        .then(function (translator) {
          if (!translator) return null;
          return translator.translate(korean).catch(function () {
            /* Chrome may purge the on-device model mid-session; the only
               documented recovery is a fresh create(), so drop the dead
               instance and let the next dialog retry. */
            translatorPromise = null;
            return null;
          });
        })
        .then(function (english) {
          english = (english || '').trim();
          if (!english || english === korean) return;
          /* The pack download can take seconds: bail if the dialog has since
             been closed or reopened for another item, or the shopper has
             typed over the name. The draft is compared by identity — its
             .name can legally differ from the input (the browser strips
             CR/LF from scraped titles on assignment). */
          if (!els.dialog.open) return;
          if (state.draft !== draft) return;
          if (els.dialogName.value.trim() !== korean) return;

          els.dialogName.value = english;
          /* saveDialog compares against draft.name to tell an untouched field
             from an edited one, so move it in step with what is displayed. */
          draft.name = english;
          draft.nameOriginal = korean;
        })
        .catch(function () {
          // Translation failed: leave the Korean name alone.
        });
    }

    /* ------------------------------------------------- unfinished item draft */

    function setStash(draft) {
      state.stash = draft || null;
      els.resumeBtn.hidden = !state.stash;
    }

    /* Whatever is in the dialog right now, in the shape openDialog() reads. */
    function readDialog() {
      var price = parseFloat(els.dialogPrice.value);
      var name = els.dialogName.value.trim();
      return {
        url: els.dialogUrl.value.trim(),
        name: name,
        /* Same rule as saveDialog: a stashed draft keeps its Korean original
           only while the shown name is the untouched translation, so a
           resumed draft still submits the marketplace title. */
        nameOriginal:
          state.draft && name === state.draft.name
            ? state.draft.nameOriginal || null
            : null,
        price: isFinite(price) ? price : null,
        quantity: parseInt(els.dialogQty.value, 10) || 1,
        comments: els.dialogComment.value.trim(),
        secondHand: els.dialogSecondHand.checked,
        image: state.draft ? state.draft.image : null,
        domesticShipping: state.draft ? state.draft.domesticShipping : null,
        priceNote: state.draft ? state.draft.priceNote : ''
      };
    }

    function isWorthKeeping(draft) {
      return !!(draft.url || draft.name || draft.price !== null || draft.comments);
    }

    /* Escape and the close button are deliberate, so they are allowed to close
       the dialog — but what was typed is kept and offered back instead of being
       thrown away. Cancelling an edit is not a draft: that item is still in the
       list, untouched. */
    els.dialog.addEventListener('close', function () {
      if (skipStash) {
        skipStash = false;
        return;
      }
      if (state.editingId !== null) return;
      var draft = readDialog();
      /* Only overwrite the stash with something worth keeping — closing an
         empty dialog must not throw away a draft parked earlier. */
      if (isWorthKeeping(draft)) setStash(draft);
    });

    function renderDialogSubtotal() {
      var price = parseFloat(els.dialogPrice.value) || 0;
      var qty = parseInt(els.dialogQty.value, 10) || 0;
      els.dialogSubtotal.textContent = won(price * qty);
    }

    function openDialog(draft, editingIndex) {
      state.draft = draft;
      state.editingId = typeof editingIndex === 'number' ? editingIndex : null;

      els.dialogUrl.value = draft.url || '';
      els.dialogName.value = draft.name || '';
      els.dialogQty.value = draft.quantity || 1;
      els.dialogPrice.value = draft.price === null ? '' : draft.price;
      els.dialogComment.value = draft.comments || '';
      els.dialogSecondHand.checked = !!draft.secondHand;

      /* Drop the previous item's photo as well as hiding its frame — a stale
         `src` was what made a manually added item show the last product read
         from a link (HAR-238). */
      els.dialogPreview.hidden = !draft.image;
      if (draft.image) {
        els.dialogImage.src = draft.image;
      } else {
        els.dialogImage.removeAttribute('src');
      }

      /* clear as well as hide, so the previous item's note cannot linger in
         the DOM for assistive tech */
      els.dialogPriceNote.textContent = draft.priceNote || '';
      els.dialogPriceNote.hidden = !draft.priceNote;

      els.dialogSave.textContent =
        state.editingId === null ? 'Add to list' : 'Save changes';
      renderDialogSubtotal();
      openOverlay(els.dialog);
      showNameInEnglish();
    }

    function saveDialog() {
      var name = els.dialogName.value.trim();
      var price = parseFloat(els.dialogPrice.value);
      var qty = parseInt(els.dialogQty.value, 10);

      if (!name) {
        els.dialogName.focus();
        els.dialogName.reportValidity();
        return;
      }
      if (!isFinite(price) || price <= 0) {
        els.dialogPrice.focus();
        els.dialogPrice.setCustomValidity('Enter the price in Korean Won.');
        els.dialogPrice.reportValidity();
        els.dialogPrice.setCustomValidity('');
        return;
      }
      if (!isFinite(qty) || qty < 1) qty = 1;

      var item = {
        id:
          state.editingId === null
            ? state.nextId++
            : state.items[state.editingId].id,
        url: normalizeUrl(els.dialogUrl.value),
        name: name,
        /* Shopper typed over the translated name: their wording is the order,
           so drop the Korean original rather than submitting it instead. */
        nameOriginal:
          name === state.draft.name ? state.draft.nameOriginal || null : null,
        price: price,
        quantity: qty,
        comments: els.dialogComment.value.trim(),
        secondHand: els.dialogSecondHand.checked,
        image: state.draft.image || null,
        domesticShipping: state.draft.domesticShipping
      };

      if (state.editingId === null) {
        state.items.push(item);
      } else {
        state.items[state.editingId] = item;
      }

      skipStash = true;
      closeOverlay(els.dialog);
      setStash(null);
      renderItems();
      els.urlInput.value = '';
    }

    /* ------------------------------------------------------------- preview */

    function setBusy(button, label, busy, busyText) {
      button.disabled = busy;
      button.classList.toggle('is-busy', busy);
      if (!label) return;
      if (busy) {
        label.dataset.original = label.textContent;
        label.textContent = busyText;
      } else if (label.dataset.original) {
        label.textContent = label.dataset.original;
      }
    }

    function showPasteError(message) {
      els.pasteError.textContent = message;
      els.pasteError.hidden = !message;
    }

    function draftFromPreview(preview, url) {
      var priceNote = '';
      var price = null;

      if (typeof preview.priceKrw === 'number') {
        price = preview.priceKrw;
        if (preview.conversionRate) {
          priceNote =
            'Converted from ' + preview.price.toLocaleString('en-US') + ' ' +
            preview.currency + ' at 1 ' + preview.currency + ' ≈ ' +
            preview.conversionRate.toFixed(2) + ' KRW. Please double-check.';
        }
      } else if (typeof preview.price === 'number') {
        /* Price found but we could not express it in KRW — showing it as-is
           would silently be treated as Won, so make the shopper confirm. */
        priceNote =
          'We found ' + preview.price.toLocaleString('en-US') +
          (preview.currency ? ' ' + preview.currency : '') +
          ' on the page but could not convert it. Please enter the KRW amount.';
      } else {
        priceNote = 'We could not read a price — please enter it in KRW.';
      }

      return {
        url: preview.url || url,
        name: preview.title || '',
        /* Set only if the browser translates the name for display (see
           showNameInEnglish); holds the Korean we still submit. */
        nameOriginal: null,
        price: price,
        quantity: 1,
        comments: '',
        secondHand: !!preview.secondHand,
        image: preview.image || null,
        domesticShipping:
          typeof preview.domesticShipping === 'number'
            ? preview.domesticShipping
            : null,
        priceNote: priceNote
      };
    }

    /* What openDialog reads, for an item already on the list. */
    function draftFromItem(item) {
      return {
        url: item.url,
        name: item.name,
        nameOriginal: item.nameOriginal || null,
        price: item.price,
        quantity: item.quantity,
        comments: item.comments,
        secondHand: item.secondHand,
        image: item.image,
        domesticShipping: item.domesticShipping,
        priceNote: ''
      };
    }

    function addFromUrl() {
      var url = els.urlInput.value.trim();
      showPasteError('');

      if (!url) {
        els.urlInput.focus();
        return;
      }
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }

      setBusy(els.addBtn, els.addLabel, true, 'Reading…');

      fetch(PREVIEW_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok || !result.data || result.data.ok !== true) {
            /* A shop we cannot read is not a dead end: open the dialog empty
               so the shopper can fill it in the way the old form required. */
            showPasteError(
              (result.data && result.data.message) ||
                'We could not read that link.' +
                ' Fill the details in below and we will take it from there.'
            );
            openDialog(draftFromPreview({ url: url }, url));
            return;
          }
          openDialog(draftFromPreview(result.data, url));
        })
        .catch(function () {
          showPasteError(
            'We could not reach our product lookup service.' +
              ' Please fill the details in manually.'
          );
          openDialog(draftFromPreview({ url: url }, url));
        })
        .then(function () {
          setBusy(els.addBtn, els.addLabel, false);
        });
    }

    /* -------------------------------------------------------- country rules */

    function applyCountryRules() {
      var country = selectedCountry();
      /* The live quote prices express shipping only. */
      var allowed = liveQuote
        ? ['dhl/fedex']
        : METHODS_BY_COUNTRY[country] || DEFAULT_METHODS;

      Array.prototype.forEach.call(els.method.options, function (option) {
        var permitted = allowed.indexOf(option.value) !== -1;
        option.hidden = !permitted;
        option.disabled = !permitted;
      });

      /* Always snap to the destination's preferred courier, the way the old
         form did — it is the one we actually quote for, and the two differ by
         USD$14 on the base rate. */
      els.method.value = allowed[0];

      var taxed = TAX_COUNTRIES.indexOf(country) !== -1;
      els.tax.hidden = !taxed;
      if (taxed) {
        els.taxCopy.textContent =
          'Parcels to ' + country + ' are subject to customs tax, payable to' +
          ' the courier when the parcel arrives. We cannot refund a parcel that' +
          ' is refused after it ships.';
      } else {
        els.taxAgree.checked = false;
      }
      renderSummary();
      scheduleQuote();
    }

    /* -------------------------------------------------------------- submit */

    function validateDetails() {
      if (!state.items.length) return 'Add at least one item first.';
      if (!els.name.value.trim()) {
        els.name.focus();
        return 'Please enter your name.';
      }
      if (!isValidEmail(els.email.value.trim())) {
        els.email.focus();
        return 'Please enter a valid email address.';
      }
      if (!els.country.value) {
        els.country.focus();
        return 'Please choose your shipping country.';
      }
      if (!els.tax.hidden && !els.taxAgree.checked) {
        els.taxAgree.focus();
        return 'Please confirm you agree to pay customs tax.';
      }
      return null;
    }

    function openReview() {
      var problem = validateDetails();
      els.submitError.hidden = !problem;
      if (problem) {
        els.submitError.textContent = problem;
        return;
      }

      var e = estimate();
      var quote = freshQuote();
      var rows = state.items
        .map(function (item) {
          return (
            '<tr><td>' + escapeHtml(item.name) +
            (item.comments
              ? '<br><span class="harum-ps__review-note">' +
                escapeHtml(item.comments) + '</span>'
              : '') +
            '</td><td>' + item.quantity + '</td><td>' +
            won(item.price * item.quantity) + '</td>' +
            /* the server prices the goods as one line each, so a second
               per-item figure here would only invite disagreement */
            (quote ? '' : '<td>' + money(itemUsd(item)) + '</td>') + '</tr>'
          );
        })
        .join('');

      var head =
        '<table class="harum-ps__review-table"><thead><tr>' +
        '<th>Item</th><th>Qty</th><th>Price</th>' +
        (quote ? '' : '<th>Est. USD</th>') +
        '</tr></thead><tbody>' + rows + '</tbody></table>';

      if (quote) {
        els.reviewBody.innerHTML =
          head +
          '<dl class="harum-ps__review-totals">' +
          quote.lines
            .map(function (line) {
              return '<div><dt>' + escapeHtml(line.label) + '</dt><dd>' +
                money(line.usd) + '</dd></div>';
            })
            .join('') +
          '<div class="harum-ps__review-total"><dt>' + totalLabel(quote) + '</dt><dd>' +
          money(quote.total) + '</dd></div></dl>' +
          quoteMeta(quote, false)
            .map(function (text) {
              return '<p class="harum-ps__review-foot">' + escapeHtml(text) + '</p>';
            })
            .join('') +
          '<p class="harum-ps__review-foot">Sent to ' +
          escapeHtml(els.email.value.trim()) + '</p>';
        openOverlay(els.review);
        return;
      }

      els.reviewBody.innerHTML =
        head +
        (liveQuote
          ? '<p class="harum-ps__review-foot">Rough estimate: we could not get a live quote.</p>'
          : '') +
        '<dl class="harum-ps__review-totals">' +
        '<div><dt>Items</dt><dd>' + money(e.items) + '</dd></div>' +
        (e.secondHandCount
          ? '<div><dt>Second-hand handling (' + e.secondHandCount +
            ')</dt><dd>' + money(e.secondHand) + '</dd></div>'
          : '') +
        '<div><dt>Shipping (' + billableWeight() + ' KG, ' +
        escapeHtml(els.method.value.toUpperCase()) + ')</dt><dd>' +
        money(e.shipping) + '</dd></div>' +
        '<div><dt>Service fee</dt><dd>' + money(e.service) + '</dd></div>' +
        '<div class="harum-ps__review-total"><dt>Estimated total</dt><dd>' +
        money(e.total) + '</dd></div></dl>' +
        '<p class="harum-ps__review-foot">Sent to ' +
        escapeHtml(els.email.value.trim()) + '</p>';

      openOverlay(els.review);
    }

    function submitOrder() {
      var e = estimate();
      var quote = freshQuote();
      var totalText = money(quote ? quote.total : e.total) + ' (Estimation only)';
      /* A blank weight is sent as 0 ("not given") when the shopper was
         quoted without one, instead of pretending they entered 1 KG. */
      var weightGiven = parseFloat(els.weight.value) > 0;

      var payload = {
        name: els.name.value.trim(),
        email: els.email.value.trim(),
        country: selectedCountry(),
        shipMethod: submitMethod(),
        estimateWeight: liveQuote && !weightGiven ? 0 : billableWeight(),
        bunjangEstimate:
          money(e.secondHand) + ' for ' + e.secondHandCount +
          ' product(s) (Estimation only)',
        totalEstimate: totalText,
        totalEstimateShown: totalText,
        taxAgree: els.tax.hidden ? 'N/A' : els.taxAgree.checked ? 'Yes' : 'No',
        products: state.items.map(function (item) {
          return {
            itemUrl: item.url,
            /* Korean original when we translated the name for display, so the
               payload stays exactly what CS and the emails expected before. */
            itemName: item.nameOriginal || item.name,
            price: item.price,
            quantity: item.quantity,
            comments: item.comments,
            secondHand: item.secondHand,
            domesticShipping: item.domesticShipping
          };
        })
      };

      if (isoCountry) payload.countryCode = els.country.value;

      var confirmLabel = els.reviewConfirm.querySelector('.btn__text');
      setBusy(els.reviewConfirm, confirmLabel, true, 'Sending…');

      fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('submit failed: ' + res.status);
          closeOverlay(els.review);
          openOverlay(els.done);
          state.items = [];
          setStash(null);
          renderItems();
        })
        .catch(function () {
          closeOverlay(els.review);
          els.submitError.hidden = false;
          els.submitError.textContent =
            'We could not send your request. Please try again, or email' +
            ' support@harum.io and we will take it from there.';
        })
        .then(function () {
          setBusy(els.reviewConfirm, confirmLabel, false);
        });
    }

    /* -------------------------------------------------------------- events */

    /* HAR-249: one box takes both a product link and a keyword. A link starts
       the paste flow as before; anything else opens the external search
       (Bunjang / Naver via searchkorea - see harum-external-search.js) in a
       new tab, so a half-built request here is never lost. The button label
       follows what is typed so the shopper can see which will happen. */
    var addLabelText = els.addLabel.textContent;
    var searchLabelText = els.addBtn.getAttribute('data-ps-search-label') || 'Search';
    var searchUrl = root.dataset.searchUrl || '/search';

    function looksLikeUrl(value) {
      var text = (value || '').trim();
      if (!text || /\s/.test(text)) return false;
      if (/^https?:\/\//i.test(text)) return true;
      /* "m.bunjang.co.kr/products/1", "musinsa.com/app/goods/2": a host with a dot. */
      return /^(www\.)?[\w-]+(\.[\w-]+)+([/?#]|$)/i.test(text);
    }

    function syncAddLabel() {
      var text = els.urlInput.value.trim();
      els.addLabel.textContent = text && !looksLikeUrl(text) ? searchLabelText : addLabelText;
    }

    function addOrSearch() {
      var text = els.urlInput.value.trim();
      if (!text) {
        els.urlInput.focus();
        return;
      }
      if (looksLikeUrl(text)) {
        addFromUrl();
        return;
      }
      showPasteError('');
      window.open(searchUrl + '?q=' + encodeURIComponent('korea-' + text), '_blank', 'noopener');
    }

    els.addBtn.addEventListener('click', addOrSearch);
    els.urlInput.addEventListener('input', syncAddLabel);
    els.urlInput.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        addOrSearch();
      }
    });

    /* HAR-249: "Start request" buttons elsewhere on the page (the CTA band at
       the bottom) bring the shopper back up to this box. */
    document.querySelectorAll('[data-harum-ps-start]').forEach(function (button) {
      if (button.dataset.harumPsStartBound) return;
      button.dataset.harumPsStartBound = '1';
      button.addEventListener('click', function (event) {
        event.preventDefault();
        els.urlInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        els.urlInput.focus({ preventScroll: true });
      });
    });

    /* A clean slate every time (HAR-238): reading the paste field here meant
       the link of a product the shopper had just abandoned came along. */
    els.manualBtn.addEventListener('click', function () {
      openDialog({
        url: '',
        name: '',
        nameOriginal: null,
        price: null,
        quantity: 1,
        comments: '',
        secondHand: false,
        image: null,
        domesticShipping: null,
        priceNote: ''
      });
    });

    els.resumeBtn.addEventListener('click', function () {
      if (!state.stash) return;
      var draft = state.stash;
      setStash(null);
      openDialog(draft);
    });

    els.itemList.addEventListener('click', function (event) {
      var editIndex = event.target.getAttribute('data-edit');
      var removeIndex = event.target.getAttribute('data-remove');
      var matchRemoveIndex = event.target.getAttribute('data-match-remove');

      if (editIndex !== null) {
        openDialog(draftFromItem(state.items[Number(editIndex)]), Number(editIndex));
      } else if (removeIndex !== null) {
        state.items.splice(Number(removeIndex), 1);
        renderItems();
      } else if (matchRemoveIndex !== null) {
        state.items.splice(Number(matchRemoveIndex), 1);
        renderItems();
      }
    });

    els.qtyUp.addEventListener('click', function () {
      els.dialogQty.value = (parseInt(els.dialogQty.value, 10) || 0) + 1;
      renderDialogSubtotal();
    });
    els.qtyDown.addEventListener('click', function () {
      els.dialogQty.value = Math.max(1, (parseInt(els.dialogQty.value, 10) || 2) - 1);
      renderDialogSubtotal();
    });
    els.dialogQty.addEventListener('input', renderDialogSubtotal);
    els.dialogPrice.addEventListener('input', renderDialogSubtotal);
    els.dialogSave.addEventListener('click', saveDialog);
    els.dialogClose.addEventListener('click', function () {
      closeOverlay(els.dialog);
    });

    els.country.addEventListener('change', function () {
      applyCountryRules();
      persist();
    });
    els.method.addEventListener('change', renderSummary);
    if (shippingLanes && els.lanes) {
      els.lanes.addEventListener('change', function (event) {
        var target = event.target;
        if (!target || target.name !== 'harum-ps-lane') return;
        state.lane = target.value;
        state.laneNote = '';
        renderSummary();
      });
    }
    els.weight.addEventListener('input', function () {
      renderSummary();
      scheduleQuote();
      persist();
    });
    els.taxAgree.addEventListener('change', function () {
      els.submitError.hidden = true;
    });

    els.submit.addEventListener('click', openReview);
    els.reviewBack.addEventListener('click', function () {
      closeOverlay(els.review);
    });
    els.reviewClose.addEventListener('click', function () {
      closeOverlay(els.review);
    });
    els.reviewConfirm.addEventListener('click', submitOrder);
    els.doneClose.addEventListener('click', function () {
      closeOverlay(els.done);
    });

    if (els.cost) {
      root.querySelectorAll('[data-ps-cost-open]').forEach(function (button) {
        button.addEventListener('click', function () {
          openOverlay(els.cost);
        });
      });
      els.costClose.addEventListener('click', function () {
        closeOverlay(els.cost);
      });
    }

    /* ------------------------------------------------------ saved list */

    function applySaved(saved) {
      state.items = saved ? saved.items : [];
      var maxId = 0;
      state.items.forEach(function (item) {
        if (item.id > maxId) maxId = item.id;
      });
      /* Items saved before ids existed all read 0; give them real ones. */
      state.items.forEach(function (item) {
        if (!item.id) item.id = ++maxId;
      });
      state.nextId = maxId + 1;
      if (saved && saved.country) {
        var known = findCountryValue(els.country, saved.country, isoCountry);
        if (known) els.country.value = known;
      }
      if (saved) els.weight.value = saved.weight;
      applyCountryRules();
      renderItems(true);
    }

    if (persistList) {
      applySaved(loadSavedList(Date.now()));
    } else {
      applyCountryRules();
      renderItems(true);
    }
    renderRateNotes();

    /* Another tab changed the list: follow it. Held back while the item
       dialog is open, because its edit index would point at the wrong row. */
    var pendingSync = false;
    function syncFromStorage() {
      if (els.dialog.open) {
        pendingSync = true;
        return;
      }
      pendingSync = false;
      applySaved(loadSavedList(Date.now()));
    }
    if (persistList) {
      window.addEventListener('storage', function (event) {
        /* key is null when the whole storage was cleared */
        if (event.key !== null && event.key !== STORAGE_KEY) return;
        syncFromStorage();
      });
      els.dialog.addEventListener('close', function () {
        if (pendingSync) syncFromStorage();
      });
    }

    /* HARUM (HAR-235): external search result cards land here with
       ?add=<product url> — start the paste flow with it so the shopper
       arrives straight in the confirm dialog. It always adds to whatever is
       already listed; a link that is already on the list reopens that item
       instead of adding it twice. */
    function handleAddParam(search) {
      var addParam = new URLSearchParams(search).get('add');
      if (!addParam) return;
      /* Consumed: a reload must not add it again. */
      try {
        var clean = new URLSearchParams(search);
        clean.delete('add');
        var query = clean.toString();
        window.history.replaceState(
          null,
          '',
          window.location.pathname + (query ? '?' + query : '') + window.location.hash
        );
      } catch (err) {
        /* history API unavailable: harmless */
      }
      if (!/^https?:\/\//i.test(addParam)) return;
      var existing = findItemIndexByUrl(state.items, addParam);
      if (existing !== -1) {
        openDialog(draftFromItem(state.items[existing]), existing);
        return;
      }
      els.urlInput.value = addParam;
      addFromUrl();
    }
    handleAddParam(window.location.search);
  }

  function boot() {
    document
      .querySelectorAll('[data-harum-personal-shopper]')
      .forEach(function (root) {
        if (root.dataset.harumPsReady) return;
        root.dataset.harumPsReady = '1';
        init(root);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders the section without a page load. */
  document.addEventListener('shopify:section:load', boot);
})();
